import { TaskContent, TaskId, TaskType } from "@/types/content-types";
import { useState, useRef, useCallback, useEffect, useMemo, ComponentRef, forwardRef, useImperativeHandle, createContext } from "react";
import { Card, CardContent } from "../ui/card";
import { TaskContentElement } from "./TaskContent";
import { Button } from "../ui/button";
import { Values } from "./Types";
import { ScrollTargetId, useScroller } from "@/hooks/useScroller";
import { useTask } from "@/contexts/appContent/appContentHooks";
import { ReadContent } from "../ReadDialog";
import { useChapterHandler } from "@/contexts/chapterHandler";
import { CloudStatusIcon } from "../CloudStatusIcon";
import { useToast } from "@/hooks/use-toast";
import { CurrentTaskContextValue } from "@/contexts/currentTask/useCurrentTask";
import { CurrentTaskProvider } from "@/contexts/currentTask/CurrentTaskProvider";

const EMPTY = {};

type TaskFormRef = {
    inputCount: number;
};

type TaskFormProps = {
    title?: string;
    saveButtonText?: { normal: string, saveInProgress: string };
    defaultValues?: Values;
    values?: Values;
    content: TaskContent;
    taskId?: TaskId;
    lockSaveButton?: boolean;
    allowEmptyAnswers?: boolean;
    allowUnchangedAnswers?: boolean;
    preContent?: JSX.Element;
    postContent?: JSX.Element;
    onUnsavedChanged?: (unsaved: boolean) => void;
    onValuesChanged?: (values: Values) => void;
    onSave?: (values: Values) => (void | Promise<void>);
    onSaved?: () => void;
    scrollTarget?: ScrollTargetId;
    hasSaveButton?: boolean;
    disabled?: boolean;
};

const TaskForm = forwardRef<TaskFormRef, TaskFormProps>(function TaskForm({
    values,
    hasSaveButton,
    scrollTarget,
    title,
    onSaved,
    onUnsavedChanged,
    onValuesChanged,
    preContent,
    postContent,
    allowEmptyAnswers = false,
    allowUnchangedAnswers = false,
    lockSaveButton = false,
    saveButtonText,
    defaultValues = EMPTY,
    content,
    taskId,
    onSave,
    disabled,
}: TaskFormProps, ref) {
    // Filters out any nullish values accidentally included in older versions
    const memoedDefaultValues = useMemo(
        () => Object.fromEntries(
            Object.entries(defaultValues).filter(([, v]) => v != null)
        ),
        [defaultValues]
    );

    const { scrollToTarget } = useScroller();

    const taskContentRef = useRef<ComponentRef<typeof TaskContentElement>>(null);

    const [saveButtonEnabled, setSaveButtonEnabled] = useState(!content?.some(question => question.hasTextBox)); // enable the button if there are no questions
    const [saving, setSaving] = useState(false);
    const [lastSavedValues, setLastSavedValues] = useState(memoedDefaultValues);

    // saveButtonEnabled is only true on the initial call if there are no text boxes, 
    // in which case the button should say "Klar" instead of "Spara" by default.
    const normalSaveButtonText = useRef(saveButtonText?.normal ?? (saveButtonEnabled ? "Klar" : "Spara"));
    const inProgressSaveButtonText = useRef("Sparar...");

    const hasTextBoxes = taskContentRef.current?.inputCount > 0;

    useImperativeHandle(ref, () => ({
        get inputCount() { return taskContentRef.current?.inputCount; },
    }));

    const handleValuesChanged = useCallback((valueEntries: Values, comparisonValues?: string[]) => {
        onValuesChanged?.(valueEntries);

        const values = Object.values(valueEntries);
        const valueCount = values.length;

        // Empty answers are allowed or there is as many values as text boxes and all values are truthy, i.e., not empty
        const emptyCheck = allowEmptyAnswers || (valueCount === taskContentRef.current?.inputCount) && values.every((v) => Boolean(v));

        // Unchanged answers are allowed or any value has changed
        const changeCheck = allowUnchangedAnswers || Object.entries(valueEntries).some(([key, value]) => (comparisonValues?.[key] ?? lastSavedValues?.[key]) ?? "" != value);

        const enabled = !hasTextBoxes || // There are no text boxes
            (
                emptyCheck &&
                changeCheck
            );

        // console.log("updateSaveButtonEnabled", enabled);
        setSaveButtonEnabled(enabled);
        onUnsavedChanged?.(enabled);
    }, [allowEmptyAnswers, allowUnchangedAnswers, hasTextBoxes, lastSavedValues, onUnsavedChanged, onValuesChanged]);

    useEffect(() => {
        // Don't change anything or disable the save button if there are no known input fields
        // inputRefCount isn't used here due to issues with react-hooks/exhaustive-deps
        // showing a warning about inputRefCount not being in the deps list, despite it
        // being based on a ref, which shouldn't be included as a dep. 
        if (taskContentRef.current?.inputCount === 0) return;

        setLastSavedValues(memoedDefaultValues);
        setSaveButtonEnabled(false);
        // Object.entries(taskContentRef.current.inputs).forEach(([id, element]) => {
        //     if (element) element.value = memoedDefaultValues?.[id] ?? "";
        // });
    }, [memoedDefaultValues]);

    useEffect(() => {
        scrollToTarget(scrollTarget);
    }, [scrollTarget, scrollToTarget]);

    if (!content || content.length == 0) return <></>;

    return (
        <>
            <div className="grow gap-3 flex flex-col">
                {preContent}
                <TaskContentElement
                    ref={taskContentRef}
                    title={title}
                    defaultInputValues={memoedDefaultValues}
                    values={values}
                    content={content}
                    taskId={taskId}
                    onValuesChanged={handleValuesChanged}
                    disabled={disabled}
                />
                {postContent}
            </div>
            {hasSaveButton && <Button
                variant="success"
                size="sm"
                className="mt-3 mx-3 shrink-0"
                onClick={async () => {
                    setSaving(true);
                    const values = {};
                    for (const id in taskContentRef.current.inputs) {
                        const element = taskContentRef.current?.inputs[id];
                        if (!element) return;

                        if (element.type === "checkbox" || element.type === "radio") {
                            values[id] = (element as HTMLInputElement).checked.toString();
                        } else {
                            values[id] = element.value;
                        }
                    }
                    await onSave?.(values);
                    onUnsavedChanged?.(false);
                    setLastSavedValues(values);
                    if (hasTextBoxes) setSaveButtonEnabled(false);
                    setSaving(false);
                    onSaved?.();
                }}
                disabled={lockSaveButton || !saveButtonEnabled || saving}
            >
                {saving ? inProgressSaveButtonText.current : normalSaveButtonText.current}
            </Button>}
        </>
    );
});

export function TaskCard({ children }) {
    return <Card className="bg-muted/50 border-border">
        <CardContent className="p-4">
            {children}
        </CardContent>
    </Card>;
}

function checkCompleted(values: Values): boolean {
    return Object.values(values).every(v => v?.toString().trim().length > 0);
}

type TaskInfo = {
    normalSaveButtonText?: string;
    inProgressSaveButtonText?: string;
    type: TaskType;
    title: string;
};

const taskInfos: Record<TaskType, TaskInfo> = {
    "read": {
        type: "read",
        title: ":section-title",
    },
    "learn": {
        type: "learn",
        title: "Lära: :section-title",
    },
    "test": {
        type: "test",
        title: "Experiment: :section-title",
    },
    "reflect": {
        type: "reflect",
        title: "Reflektion: :section-title",
    },
    "practice": {
        type: "practice",
        title: "Tema Practise: :section-title",
        normalSaveButtonText: "Fortsätt resan",
    },
    "chapter_insights": {
        type: "chapter_insights",
        title: "Mina Insikter: Steg :step-number kapitel :chapter-number<br>:chapter-title",
        normalSaveButtonText: "Fortsätt resan",
    },
    simple_explore: {
        type: "simple_explore",
        title: "Utforska: :section-title"
    },
    simple_test_and_reflect: {
        type: "simple_test_and_reflect",
        title: "Prova och reflektera: :section-title"
    },
    simple_practise: {
        type: "simple_practise",
        title: "Practise: :section-title"
    }
};

type TaskRef = {
    inputCount: number;
};
type TaskProps = {
    taskId: TaskId;
    scrollTarget: ScrollTargetId;
};
const Task = forwardRef<TaskRef, TaskProps>(function Task({ taskId, scrollTarget }, ref) {
    const { data: userData, task, save, refetchData, dataStatus, } = useTask({ taskId: taskId });
    const { currentSection } = useChapterHandler();
    const { toast } = useToast();

    // The form ref is only attached after the first render, so inputCount is
    // undefined until then. Flipping this after mount changes the context value,
    // which re-renders consumers (e.g. the continue journey button) with the real count.
    const [formMounted, setFormMounted] = useState(false);
    useEffect(() => { setFormMounted(true); }, []);

    const taskContextValue = useMemo<CurrentTaskContextValue>(() => ({
        get inputCount() { return formMounted ? taskFormRef.current?.inputCount : undefined; },
    }), [formMounted]);

    const taskFormRef = useRef<ComponentRef<typeof TaskForm>>(null);

    useImperativeHandle(ref, () => ({
        get inputCount() { return taskFormRef.current?.inputCount; },
    }));

    useEffect(() => {
        refetchData();
    }, [refetchData]);

    // chapter_insights is always the last task in its section.
    // practice is the last task in the section if the sections 
    // is not the last in the chapter. simple_practise always has the button
    // so that it can read the task's input count from the task context.
    const continueJourney = task?.type === "chapter_insights"
        || task?.type === "simple_practise"
        || (task?.type === "practice" && currentSection?.section.next_section);

    const contentToShow = useMemo(() => {
        const arr = [...(task?.content ?? [])];
        if (continueJourney) {
            arr.push({ body: "::continue-journey-button[Fortsätt resan]" });
        }
        return arr;
    }, [continueJourney, task?.content]);


    if (!task) {
        return "Hämtar uppgiften...";
    }

    const info = taskInfos[task.type];

    return <CurrentTaskProvider value={taskContextValue} >
        <div className="absolute top-2 left-2">
            <CloudStatusIcon status={dataStatus} />
        </div>
        {
            task.type === "read"
                ? <ReadContent
                    title={info?.title}
                    text={currentSection?.section.text}
                />
                : <TaskForm
                    ref={taskFormRef}
                    scrollTarget={scrollTarget}
                    title={info?.title}
                    content={contentToShow}
                    taskId={task.id}
                    allowEmptyAnswers={true}
                    allowUnchangedAnswers={info?.title === "Fortsätt resan"} // TODO: Don't hardcode this
                    values={userData?.data}

                    onValuesChanged={(values) => {
                        (async () => {
                            const state = await save({
                                data: { data: values, version: 2 },
                                // simple_practise tasks are only completed by the continue journey button
                                completed: task.type !== "simple_practise" && checkCompleted(values),
                            });
                            if (state.dataStatus === "error") {
                                toast({
                                    title: "Lyckades inte spara",
                                    description: "Vänligen försök igen senare",
                                    variant: "destructive"
                                });
                            }
                        })();
                    }}
                />
        }

    </CurrentTaskProvider >;
});

export { Task, TaskForm };